<script setup lang="ts">
import { computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Panel from 'primevue/panel';
import { toTitleCase } from '@/utils/kyc';

const props = defineProps<{
  jsonData: object | any[] | null | undefined;
}>();

const isArray = computed(() => Array.isArray(props.jsonData));
const isObject = computed(() => props.jsonData !== null && typeof props.jsonData === 'object' && !Array.isArray(props.jsonData));

const tableData = computed(() => {
  if (isArray.value && props.jsonData) {
    return props.jsonData as any[];
  }
  return [];
});

const tableColumns = computed(() => {
  if (!isArray.value || tableData.value.length === 0) {
    return [];
  }
  // Filter out non-object items, just in case array is mixed
  const firstObject = tableData.value.find(item => typeof item === 'object' && item !== null);
  if (!firstObject) return [];

  return Object.keys(firstObject).map(key => ({
    field: key,
    header: toTitleCase(key.replace(/_/g, ' ')),
  }));
});

const objectData = computed(() => {
  if (isObject.value && props.jsonData) {
    return Object.entries(props.jsonData as object).map(([key, value]) => ({
      key: toTitleCase(key.replace(/_/g, ' ')),
      value: value,
    }));
  }
  return [];
});
</script>

<template>
  <div v-if="jsonData && (tableData.length > 0 || objectData.length > 0)" class="mt-4 pt-3 border-t border-gray-200">
    <Panel :toggleable="true" :collapsed="false">
      <template #header>
        <h5 class="text-xs font-semibold text-gray-700">Detalhes</h5>
      </template>
      <div v-if="isArray" class="mt-2">
        <DataTable :value="tableData" size="small" stripedRows responsiveLayout="scroll">
          <Column v-for="col of tableColumns" :key="col.field" :field="col.field" :header="col.header" class="text-xs" />
        </DataTable>
      </div>
      <div v-else-if="isObject" class="mt-2">
        <dl class="text-xs grid grid-cols-2 gap-x-4 gap-y-1">
          <template v-for="item in objectData" :key="item.key">
            <dt class="font-medium text-gray-600 truncate">{{ item.key }}</dt>
            <dd class="text-gray-800 text-right">{{ item.value }}</dd>
          </template>
        </dl>
      </div>
    </Panel>
  </div>
</template>
